import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import dayjs from 'dayjs';
import {
  Alert,
  alpha,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import PlaylistPlayOutlinedIcon from '@mui/icons-material/PlaylistPlayOutlined';

import { Item } from '../../../styles/common';
import { CrmPageFrame } from '../CrmPageFrame';
import { LogCallDialog } from '../calls/LogCallDialog';
import { forgetCallData } from '../calls/LogCallForm';
import { dialledNumber } from '../contactDetail/edit/contactEdit';
import { ContactPager } from '../contacts/ContactListControls';
import { DEFAULT_PAGE_SIZE } from '../contacts/contactList';
import { FollowUpsTable } from './FollowUpsTable';
import {
  describeFollowUpCount,
  describeFollowUpsError,
  FollowUpRow,
  FollowUpsState,
  parseFollowUpsState,
  personName,
  queuePathForFollowUps,
  setByChoices,
  toFollowUpsParams,
  VIEWS,
  withFollowUpsChange,
} from './followUps';
import { useFollowUpsPage } from './useFollowUpsPage';

const n = (value: number) => value.toLocaleString('en-US');

/** Rows in the table's shape while the first page loads. */
const LoadingRows = () => (
  <Box role="status" aria-label="Loading follow-ups…" sx={{ px: 2, py: 0.5 }}>
    {Array.from({ length: 6 }, (_, index) => (
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: '1px solid #eef1f5' }}>
        <Skeleton variant="text" sx={{ flex: '0 0 12%' }} />
        <Skeleton variant="text" sx={{ flex: '0 0 18%' }} />
        <Skeleton variant="text" sx={{ flex: '0 0 10%' }} />
        <Skeleton variant="rounded" width={80} height={20} />
        <Skeleton variant="text" sx={{ flex: 1 }} />
      </Box>
    ))}
  </Box>
);

/**
 * Follow-ups (card 22): calls with a follow-up date that has passed and that
 * nobody has closed, the earliest first, every admin's or only some. "Coming
 * up" lists the open ones for a later date.
 *
 * Each row offers the call back, which closes the follow-up, and closing it by
 * hand. People who cannot be called now stay listed, greyed, with why: their
 * follow-up is still open until someone closes it. "Call them in the queue"
 * works through the callable ones one at a time.
 *
 * Every choice is in the URL. Requests: on opening and on a change of view or
 * "Set by", a page with the two views' counts and who set them; on paging, the
 * page alone; after a call or a close, everything again.
 */
export const CrmFollowUps = () => {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => parseFollowUpsState(params), [params]);
  const client = useApolloClient();
  const [logging, setLogging] = useState<FollowUpRow | null>(null);
  /** Moves "now" after a call or a close, so what came due meanwhile is listed too. */
  const [asked, setAsked] = useState(0);

  // "Now": fixed while the view and "Set by" stay the same, so paging does not
  // change the request's variables.
  const filterParams = toFollowUpsParams({ ...state, page: 0, pageSize: DEFAULT_PAGE_SIZE }).toString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => dayjs(), [filterParams, asked]);

  const show = useCallback((next: FollowUpsState) => setParams(toFollowUpsParams(next)), [setParams]);
  const change = useCallback((partial: Partial<FollowUpsState>) => show(withFollowUpsChange(state, partial)), [show, state]);

  const { rows, loading, error, hasNextPage, counts, reset } = useFollowUpsPage(state, now);
  const myAdminId = counts?.byCaller.find((caller) => caller.isYou)?.adminId ?? null;
  const total = counts ? (state.view === 'due' ? counts.due : counts.upcoming) : undefined;
  const admins = setByChoices(counts?.byCaller);
  const firstLoad = loading && rows.length === 0;
  const whose = state.by === 'me' ? ' set by you' : state.by ? ' set by this admin' : '';

  const changed = () => {
    forgetCallData(client.cache);
    reset();
    setAsked((count) => count + 1);
  };

  const logged = () => {
    setLogging(null);
    changed();
  };

  const blockedOf = (row: FollowUpRow) => (row.contact?.doNotContact ? 'doNotContact' : row.contact?.doNotCall ? 'doNotCall' : null);

  return (
    <CrmPageFrame title="Follow-ups" description="Calls with a follow-up date that has passed and nobody has closed.">
      <Item sx={{ p: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: { xs: 1.5, sm: 2 },
            pt: 1,
          }}
        >
          <Tabs
            value={state.view}
            onChange={(_, view) => change({ view })}
            aria-label="Which follow-ups"
            sx={{ minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, px: 1.5 } }}
          >
            {VIEWS.map((view) => {
              const count = counts ? (view.id === 'due' ? counts.due : counts.upcoming) : undefined;
              return (
                <Tab
                  key={view.id}
                  value={view.id}
                  title={view.hint}
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      {view.label}
                      {count !== undefined && (
                        <Box
                          component="span"
                          sx={{
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: '0.75rem',
                            px: 0.75,
                            borderRadius: 999,
                            bgcolor: view.id === 'due' && count > 0 ? '#fbbf24' : '#eef1f5',
                            color: '#111827',
                          }}
                        >
                          {n(count)}
                        </Box>
                      )}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>

          <FormControl size="small" sx={{ minWidth: 180, my: 1 }}>
            <InputLabel id="crm-follow-ups-by" shrink>
              Set by
            </InputLabel>
            <Select
              labelId="crm-follow-ups-by"
              label="Set by"
              notched
              value={state.by}
              onChange={(event) => change({ by: event.target.value })}
              displayEmpty
              renderValue={(value) =>
                value === '' ? 'Everyone' : value === 'me' ? 'You' : admins.find((admin) => admin.key === value)?.name ?? `Admin #${value.slice(1)}`
              }
            >
              <MenuItem value="">Everyone</MenuItem>
              <MenuItem value="me">You</MenuItem>
              {admins.map((admin) => (
                <MenuItem key={admin.key} value={admin.key}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                    <span>{admin.name}</span>
                    <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                      {n(admin.count)}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: { xs: 1.5, sm: 2 }, py: 1.5 }}>
          <Typography variant="h6" component="p" aria-live="polite" sx={{ fontSize: '1.125rem', fontVariantNumeric: 'tabular-nums', mr: 'auto' }}>
            {total !== undefined ? describeFollowUpCount(total, state.view) : loading ? 'Counting…' : ''}
          </Typography>
          {state.view === 'due' && Boolean(total) && (
            <Tooltip describeChild title="The call queue serves these one at a time, the earliest first, leaving out anyone who cannot be called now.">
              <Button
                component={RouterLink}
                to={queuePathForFollowUps(state.by)}
                variant="contained"
                size="small"
                startIcon={<PlaylistPlayOutlinedIcon />}
                sx={{ boxShadow: 'none' }}
              >
                {state.by === 'me' ? 'Call mine in the queue' : 'Call them in the queue'}
              </Button>
            </Tooltip>
          )}
        </Box>

        {loading ? <LinearProgress aria-label="Loading follow-ups" sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {describeFollowUpsError(error)}
          </Alert>
        )}

        {rows.length > 0 && (
          <FollowUpsTable rows={rows} now={now} myAdminId={myAdminId} stale={loading} onLog={setLogging} onClosed={changed} />
        )}

        {firstLoad && <LoadingRows />}

        {!firstLoad && !error && rows.length === 0 && (
          <Box sx={{ py: 7, px: 2, display: 'grid', justifyItems: 'center', gap: 1, textAlign: 'center' }}>
            <Box
              sx={(theme) => ({
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: 'success.main',
                mb: 1,
              })}
            >
              <EventAvailableOutlinedIcon />
            </Box>
            <Typography variant="h6" component="p">
              {state.view === 'due' ? 'Nothing due' : 'Nothing coming up'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
              {state.view === 'due'
                ? `Every follow-up${whose} whose date has come has been called back or closed.`
                : `No open follow-up${whose} is set for a later date. A follow-up is set when a call is logged.`}
            </Typography>
            {state.view === 'due' && Boolean(counts?.upcoming) && (
              <Button variant="outlined" sx={{ mt: 1 }} onClick={() => change({ view: 'upcoming' })}>
                See the {n(counts?.upcoming ?? 0)} coming up
              </Button>
            )}
          </Box>
        )}

        <Divider />
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1 }}>
          <ContactPager
            page={state.page}
            pageSize={state.pageSize}
            rowsOnPage={rows.length}
            hasNextPage={hasNextPage}
            total={total !== undefined ? { count: total, exact: true } : undefined}
            disabled={loading}
            onPage={(page) => show({ ...state, page })}
            onPageSize={(pageSize) => change({ pageSize })}
          />
        </Box>
      </Item>

      {logging && (
        <LogCallDialog
          contactId={logging.contactId}
          name={personName(logging.contact, logging.contactId)}
          blocked={blockedOf(logging)}
          number={logging.contact && !notCallableNumber(logging) ? dialledNumber(logging.contact) : null}
          onClose={() => setLogging(null)}
          onLogged={logged}
        />
      )}
    </CrmPageFrame>
  );
};

/** No number to offer "Wrong number" for: blocked people are not rung, so nothing was dialled. */
const notCallableNumber = (row: FollowUpRow) => Boolean(row.contact?.doNotCall || row.contact?.doNotContact);
